import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="dropdown"
export default class extends Controller {
  static targets = ["button", "menu", "hiddenInput", "selectedText", "searchInput", "optionsList"]

  connect() {
    // Close dropdown when clicking outside
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener("click", this.boundCloseOnClickOutside)

    // Initialize the selected state and display text based on the current hidden input value
    this.updateSelectedState()
    this.updateSelectedText()
  }

  updateSelectedText() {
    const currentValue = this.hiddenInputTarget.value
    const currentOption = this.menuTarget.querySelector(`[data-value="${currentValue}"]`)

    if (currentOption && this.hasSelectedTextTarget) {
      const display = currentOption.dataset.display
      this.selectedTextTarget.textContent = display
    }
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseOnClickOutside)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.menuTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.menuTarget.classList.remove("hidden")
    this.buttonTarget.setAttribute("aria-expanded", "true")

    // Always reset scroll position to top when opening to ensure all options are visible
    this.menuTarget.scrollTop = 0

    // Clear search input and focus it
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
      setTimeout(() => this.searchInputTarget.focus(), 100)
    }

    // Ensure all option elements are properly visible
    const allOptions = this.menuTarget.querySelectorAll("[data-value]")
    allOptions.forEach((option, index) => {
      // Remove any classes that might be hiding the options
      option.classList.remove("hidden", "invisible")
      // Force visibility with inline styles to override any CSS
      option.style.display = "list-item"
      option.style.visibility = "visible"
      option.style.opacity = "1"
      option.style.minHeight = "36px"
      option.style.height = "auto"
      option.style.listStyle = "none"

      // Use setProperty with !important to ensure these styles can't be overridden
      option.style.setProperty('min-height', '36px', 'important')
      option.style.setProperty('height', 'auto', 'important')
      option.style.setProperty('display', 'list-item', 'important')
      option.style.setProperty('padding-top', '8px', 'important')
      option.style.setProperty('padding-bottom', '8px', 'important')

      // Ensure proper text visibility without forcing ugly colors
      // Remove any conflicting styles that might hide text
      option.style.removeProperty('color')
      option.style.removeProperty('background-color')
      option.style.removeProperty('border')

      // Ensure the option has proper Tailwind classes for visibility
      option.classList.add('text-slate-900', 'dark:text-slate-100')

      // Also ensure text content spans are visible
      const textSpan = option.querySelector('span')
      if (textSpan) {
        textSpan.style.setProperty('visibility', 'visible', 'important')
        textSpan.style.setProperty('display', 'block', 'important')
        textSpan.classList.add('text-slate-900', 'dark:text-slate-100')
      }
    })

    // Show all options initially
    this.showAllOptions()

    // Update the selected state to reflect the current value
    this.updateSelectedState()
  }

  close() {
    this.menuTarget.classList.add("hidden")
    this.buttonTarget.setAttribute("aria-expanded", "false")
  }

  selectOption(event) {
    const option = event.target.closest("[data-value]")
    if (!option) return

    event.preventDefault()
    event.stopPropagation()

    const value = option.dataset.value
    const display = option.dataset.display

    // Update hidden input and display text
    this.hiddenInputTarget.value = value
    this.selectedTextTarget.textContent = display

    // Trigger change event on the hidden input to notify other controllers
    this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }))

    // Update selected state visually
    this.updateSelectedState()

    // Close dropdown
    this.close()
  }

  updateSelectedState(selectedOption) {
    const currentValue = this.hiddenInputTarget.value
    const fieldName = this.hiddenInputTarget.name

    // Remove previous selection indicators from all options in THIS dropdown only
    this.menuTarget.querySelectorAll("[data-value]").forEach((option) => {
      const checkmark = option.querySelector("span:last-child")
      if (checkmark) {
        checkmark.classList.add("hidden")
      }
      option.classList.remove("bg-indigo-100", "dark:bg-indigo-900/50")
    })

    // Find the option that matches the current value and mark it as selected in THIS dropdown only
    const currentOption = this.menuTarget.querySelector(`[data-value="${CSS.escape(currentValue)}"]`)
    if (currentOption) {
      const checkmark = currentOption.querySelector("span:last-child")
      if (checkmark) {
        checkmark.classList.remove("hidden")
      }
      currentOption.classList.add("bg-indigo-100", "dark:bg-indigo-900/50")
    }
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  filterOptions() {
    const query = this.searchInputTarget.value.toLowerCase().trim()

    if (query.length === 0) {
      this.showAllOptions()
      return
    }

    // Get all options and group headers
    const allOptions = this.menuTarget.querySelectorAll("[data-value]")
    const groupHeaders = this.menuTarget.querySelectorAll("li:not([data-value])")

    // Hide all group headers initially
    groupHeaders.forEach(header => {
      header.style.display = "none"
    })

    let visibleGroups = new Set()

    // Filter options based on search query
    allOptions.forEach(option => {
      const optionText = option.textContent.toLowerCase()
      const displayText = option.dataset.display ? option.dataset.display.toLowerCase() : ""

      if (optionText.includes(query) || displayText.includes(query)) {
        option.style.display = "list-item"

        // Find the group this option belongs to
        let currentElement = option.previousElementSibling
        while (currentElement && currentElement.hasAttribute('data-value')) {
          currentElement = currentElement.previousElementSibling
        }
        if (currentElement && !currentElement.hasAttribute('data-value')) {
          visibleGroups.add(currentElement)
        }
      } else {
        option.style.display = "none"
      }
    })

    // Show group headers that have visible options
    visibleGroups.forEach(header => {
      header.style.display = "list-item"
    })
  }

  showAllOptions() {
    // Show all options and group headers
    const allElements = this.menuTarget.querySelectorAll("li")
    allElements.forEach(element => {
      element.style.display = "list-item"
    })
  }
} 
